import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { ClientService } from '../../../services/client.service';
import { AuthService } from '../../../services/auth.service';
import { User } from '../../../models/user';
import { Client } from '../../../interfaces/cliente-interface';

export interface InventoryRow {
  clientId: string;
  companyName: string;
  chatModel: string;
  cheaperModel: string;
  toolsModel: string;
  embeddingModel: string;
  imageModel: string;
  clientGcpProjectId: string;
  aliceGcpProjectId: string;
  environment: string;
  selected: boolean;
}

@Component({
  selector: 'mm-alice-model-inventory',
  templateUrl: './alice-model-inventory.component.html',
  styleUrls: ['./alice-model-inventory.component.scss'],
})
export class AliceModelInventoryComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  loading = false;
  errorText = '';
  environment = '';
  aliceGcpProjectId = '';
  aliceGcpLocation = '';

  allRows: InventoryRow[] = [];
  filteredRows: InventoryRow[] = [];

  filterForm: UntypedFormGroup;
  bulkForm: UntypedFormGroup;

  lastDryRun: any = null;
  lastApply: any = null;
  bulkBusy = false;

  readonly slots = [
    { value: 'MODEL', label: 'Modelo LLM' },
    { value: 'MODEL_EMBEDDING', label: 'Modelo de Embedding' },
    { value: 'CHEAPER_MODEL', label: 'Modelo de LLM mais barato' },
    { value: 'TOOLS_MODEL', label: 'Modelo de Ferramentas' },
    { value: 'MODEL_IMAGE', label: 'Modelo de Geração de Imagens' },
  ];

  constructor(
    private clientService: ClientService,
    private authService: AuthService,
    private user: User,
    private fb: UntypedFormBuilder,
  ) {
    this.filterForm = this.fb.group({
      companyName: [''],
      model: [''],
      modelSlot: ['ANY'],
      clientGcp: [''],
    });
    this.bulkForm = this.fb.group({
      slot: ['MODEL'],
      matchValue: [''],
      newProvider: ['GEMINI'],
      newValue: [''],
    });
  }

  ngOnInit() {
    this.load();
    this.filterForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.applyFilters());
  }

  load() {
    this.loading = true;
    this.errorText = '';
    this.lastDryRun = null;
    this.lastApply = null;

    this.clientService.allClients()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((page: any) => {
          const clients: Client[] = page?.content || page || [];
          const aliceClients = clients.filter((c) => this.hasAlice(c));
          if (!aliceClients.length) {
            return of({ clients: aliceClients, inventory: null });
          }
          const authClient = aliceClients[0];
          return this.authenticateReportToken(authClient).pipe(
            switchMap(() =>
              forkJoin({
                clients: of(aliceClients),
                inventory: this.clientService.getAliceModelInventory().pipe(
                  catchError((err) => {
                    this.errorText = err?.error?.message || err?.message || 'Falha ao carregar inventário Alice';
                    return of(null);
                  }),
                ),
              }),
            ),
            catchError((err) => {
              this.errorText = err?.error?.message || err?.message || 'Falha na autenticação de licença Alice';
              return of({ clients: aliceClients, inventory: null });
            }),
          );
        }),
      )
      .subscribe({
        next: ({ clients, inventory }) => {
          this.environment = inventory?.environment || '';
          this.aliceGcpProjectId = inventory?.aliceGcpProjectId || '';
          this.aliceGcpLocation = inventory?.aliceGcpLocation || '';
          const byId = new Map<string, any>(
            (inventory?.items || []).map((item: any) => [String(item.clientId), item]),
          );
          this.allRows = clients.map((c) => {
            const item = byId.get(String(c.id)) || {};
            return {
              clientId: String(c.id),
              companyName: c.companyName || c.name || String(c.id),
              chatModel: item.chatModel || '',
              cheaperModel: item.cheaperModel || '',
              toolsModel: item.toolsModel || '',
              embeddingModel: item.embeddingModel || '',
              imageModel: item.imageModel || '',
              clientGcpProjectId: item.clientGcpProjectId || '',
              aliceGcpProjectId: this.aliceGcpProjectId,
              environment: this.environment,
              selected: false,
            } as InventoryRow;
          });
          this.applyFilters();
          this.loading = false;
        },
        error: (err) => {
          this.loading = false;
          this.errorText = err?.message || 'Erro ao carregar clientes';
        },
      });
  }

  private hasAlice(client: Client): boolean {
    if (client.activeModules?.some((m) => String(m).toLowerCase() === 'alice')) {
      return true;
    }
    return !!client.modules?.find(
      (m) => m?.module?.name === 'alice' && (m.module.available || m.active),
    );
  }

  private authenticateReportToken(client: Client) {
    return this.authService.licenseAuth(client.id).pipe(
      switchMap((resp: any) => {
        const token = localStorage.getItem('accessToken');
        const config = {
          clientId: client.id,
          email: resp?.client?.adminLogin || resp?.client?.email || client.email,
          token: resp.id,
        };
        return this.authService.validateLicenseAuthToken(config, token);
      }),
      switchMap((resp: any) => {
        this.user.setReportToken(resp.accessToken);
        return of(resp);
      }),
    );
  }

  applyFilters() {
    const f = this.filterForm.value;
    const name = (f.companyName || '').toLowerCase().trim();
    const model = (f.model || '').toLowerCase().trim();
    const slot = f.modelSlot || 'ANY';
    const gcp = (f.clientGcp || '').toLowerCase().trim();

    this.filteredRows = this.allRows.filter((row) => {
      if (name && !row.companyName.toLowerCase().includes(name)) {
        return false;
      }
      if (gcp && !(row.clientGcpProjectId || '').toLowerCase().includes(gcp)
        && !(row.aliceGcpProjectId || '').toLowerCase().includes(gcp)) {
        return false;
      }
      if (model) {
        const values = this.modelValuesForSlot(row, slot).map((v) => v.toLowerCase());
        if (!values.some((v) => v.includes(model))) {
          return false;
        }
      }
      return true;
    });
  }

  private modelValuesForSlot(row: InventoryRow, slot: string): string[] {
    switch (slot) {
      case 'MODEL':
        return [row.chatModel];
      case 'CHEAPER_MODEL':
        return [row.cheaperModel];
      case 'TOOLS_MODEL':
        return [row.toolsModel];
      case 'MODEL_EMBEDDING':
        return [row.embeddingModel];
      case 'MODEL_IMAGE':
        return [row.imageModel];
      default:
        return [
          row.chatModel,
          row.cheaperModel,
          row.toolsModel,
          row.embeddingModel,
          row.imageModel,
        ];
    }
  }

  get selectedIds(): string[] {
    return this.filteredRows.filter((r) => r.selected).map((r) => r.clientId);
  }

  toggleSelectFiltered(checked: boolean) {
    this.filteredRows.forEach((r) => (r.selected = checked));
  }

  slotLabel(slot: string): string {
    return this.slots.find((s) => s.value === slot)?.label || slot || '—';
  }

  clientName(clientId: string): string {
    const id = String(clientId);
    return this.allRows.find((r) => r.clientId === id)?.companyName || id;
  }

  formatModel(provider?: string, value?: string): string {
    const p = (provider || '').trim();
    const v = (value || '').trim();
    if (p && v) {
      return `${p} / ${v}`;
    }
    return v || p || '—';
  }

  skipReasonPt(reason?: string): string {
    switch (reason) {
      case 'no model config row':
        return 'cliente sem configuração deste slot';
      case 'value mismatch':
        return 'valor atual diferente do filtro “só se valor atual”';
      default:
        return reason || 'ignorado';
    }
  }

  plannedResults(resp: any): any[] {
    return (resp?.results || []).filter((r: any) => !r.skipped && r.success !== false && !r.errorMessage);
  }

  skippedResults(resp: any): any[] {
    return (resp?.results || []).filter((r: any) => r.skipped);
  }

  failedResults(resp: any): any[] {
    return (resp?.results || []).filter((r: any) => !r.skipped && (r.success === false || r.errorMessage));
  }

  simulationSummary(resp: any): { fromText: string; toText: string } | null {
    const planned = this.plannedResults(resp);
    const sample = planned[0];
    const v = this.bulkForm.value;
    const toText = this.formatModel(v.newProvider || 'GEMINI', v.newValue);
    if (sample) {
      return {
        fromText: this.formatModel(sample.oldProvider, sample.oldValue),
        toText: this.formatModel(sample.newProvider, sample.newValue) || toText,
      };
    }
    if (v.matchValue) {
      return {
        fromText: this.formatModel(undefined, v.matchValue),
        toText,
      };
    }
    return { fromText: '(valor atual de cada cliente)', toText };
  }

  runBulk(dryRun: boolean) {
    const ids = this.selectedIds;
    const v = this.bulkForm.value;
    if (!ids.length) {
      this.errorText = 'Selecione ao menos um cliente';
      return;
    }
    if (!v.newValue?.trim()) {
      this.errorText = 'Informe o novo valor do modelo';
      return;
    }
    this.errorText = '';
    this.bulkBusy = true;
    this.clientService.bulkUpdateAliceModels({
      clientIds: ids,
      slot: v.slot,
      matchValue: v.matchValue?.trim() || undefined,
      newProvider: v.newProvider?.trim() || 'GEMINI',
      newValue: v.newValue.trim(),
      dryRun,
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp) => {
          this.bulkBusy = false;
          if (dryRun) {
            this.lastDryRun = resp;
            this.lastApply = null;
          } else {
            this.lastApply = resp;
            this.load();
          }
        },
        error: (err) => {
          this.bulkBusy = false;
          this.errorText = err?.error?.message || err?.message || 'Falha no bulk';
        },
      });
  }
}
